import { useMemo, useCallback, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import styles from "./styles.module.css";

// Types
interface FinalResult {
  project_name: string;
  serial_number: string;
  quantity: number;
  is_range: boolean;
  final_value: string;
  flow_type?: "New" | "Existing";
}

interface ParsedContent {
  finalAnswer: {
    line_items: any[];
    final_result: FinalResult;
  };
  question: string;
}

interface ManpowerCostDetailedIntakeProps {
  parsedContent: ParsedContent;
}

export default function ManpowerCostDetailedIntake({
  parsedContent,
}: ManpowerCostDetailedIntakeProps): JSX.Element {
  const dispatch = useAppDispatch();

  // Extract data from parsed content
  const { final_result, line_items = [] } = parsedContent?.finalAnswer || {};

  // Get current agent and session info
  const currentAgent = useSelector(
    (state: RootState) => state.agents?.currentAgent,
  );
  const agentLoading = useSelector((state: RootState) => state.user.loading);

  const sessionId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("sessionId");
  }, []);

  // State to track if buttons have been clicked
  const [isActionTaken, setIsActionTaken] = useState<boolean>(false);

  // Format the final value with proper currency formatting (supports decimals)
  const formattedValue = useMemo(() => {
    if (!final_result?.final_value) return "0";

    const valueStr = final_result.final_value.toString();

    // Check if it's a range (contains " - ")
    if (valueStr.includes(" - ")) {
      const [minValue, maxValue] = valueStr.split(" - ");
      const formattedMin = parseFloat(minValue).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
      const formattedMax = parseFloat(maxValue).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
      return `${formattedMin} - ${formattedMax}`;
    }

    // Single value (supports decimals)
    const value = parseFloat(valueStr);
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }, [final_result?.final_value]);

  // Extract unique job families from line items
  const jobFamilies = useMemo(() => {
    if (!line_items || line_items.length === 0) return "Engineering";

    const uniqueFamilies = Array.from(
      new Set(
        line_items
          .map((item: any) => item.job_family)
          .filter((family: string) => family && family.trim() !== ""),
      ),
    );

    return uniqueFamilies.length > 0
      ? uniqueFamilies.join(", ")
      : "Engineering";
  }, [line_items]);

  // Determine the result type text
  const resultTypeText = useMemo(() => {
    if (!final_result) return "";
    if (final_result.is_range) {
      return `${final_result.quantity} resources — range shown.`;
    }
    return `${final_result.quantity} resource(s) — exact lumpsum shown.`;
  }, [final_result]);

  // Handle Save & Start New action
  const handleSaveAndStartNew = useCallback(() => {
    if (!currentAgent) return;

    setIsActionTaken(true); // Disable buttons permanently

    const questionId = uuid();
    const timestamp = new Date().toISOString();

    const payload = {
      agentId: currentAgent.id,
      message: "Save and start new",
      questionId,
      timestamp,
      agentEndpoint: currentAgent.endpoint,
      sessionId,
      action: "save_and_start_new",
    };

    dispatch(sendMessageToAgent(payload));
  }, [currentAgent, sessionId, dispatch]);

  // Handle Save & Close action
  const handleSaveAndClose = useCallback(() => {
    if (!currentAgent) return;

    setIsActionTaken(true); // Disable buttons permanently

    const questionId = uuid();
    const timestamp = new Date().toISOString();

    const payload = {
      agentId: currentAgent.id,
      message: "Save & Close",
      questionId,
      timestamp,
      agentEndpoint: currentAgent.endpoint,
      sessionId,
      action: "save_and_close",
    };

    dispatch(sendMessageToAgent(payload));
  }, [currentAgent, sessionId, dispatch]);

  // Handle Overwrite Original action (for existing estimation)
  const handleOverwriteOriginal = useCallback(() => {
    if (!currentAgent) return;

    setIsActionTaken(true);

    const questionId = uuid();
    const timestamp = new Date().toISOString();

    const payload = {
      agentId: currentAgent.id,
      message: "Overwrite original",
      questionId,
      timestamp,
      agentEndpoint: currentAgent.endpoint,
      sessionId,
      action: "overwrite_original",
    };

    dispatch(sendMessageToAgent(payload));
  }, [currentAgent, sessionId, dispatch]);

  // Handle Save as New Version action (for existing estimation)
  const handleSaveAsNewVersion = useCallback(() => {
    if (!currentAgent) return;

    setIsActionTaken(true);

    const questionId = uuid();
    const timestamp = new Date().toISOString();

    const payload = {
      agentId: currentAgent.id,
      message: "Save as new version",
      questionId,
      timestamp,
      agentEndpoint: currentAgent.endpoint,
      sessionId,
      action: "save_as_new_version",
    };

    dispatch(sendMessageToAgent(payload));
  }, [currentAgent, sessionId, dispatch]);

  if (!final_result) {
    return (
      <Box className={styles.container}>
        <Typography color="error">No result data available</Typography>
      </Box>
    );
  }

  // Check if this is an existing estimation flow
  const isExistingFlow = final_result.flow_type === "Existing";

  return (
    <Box className={styles.container}>
      {/* Header */}
      <Box className={styles.header}>
        <Box className={styles.headerIcon}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#00A3A3">
            <circle cx="12" cy="12" r="3" />
          </svg>
        </Box>
        <Typography variant="subtitle2" className={styles.headerText}>
          MANPOWER COST CALCULATOR
        </Typography>
      </Box>

      {/* Summary Text */}
      <Typography className={styles.summaryText}>
        For <strong>{final_result.project_name}</strong>,{" "}
        <strong>{final_result.quantity}</strong> resource(s) across{" "}
        {jobFamilies} were priced.
      </Typography>

      {/* Result Card */}
      <Box className={styles.resultCard}>
        <Typography className={styles.resultLabel}>
          {final_result.is_range ? "Estimated Range" : "Exact Lumpsum"}
        </Typography>
        <Typography className={styles.resultValue}>
          USD {formattedValue}
        </Typography>
        <Typography className={styles.resultDescription}>
          {resultTypeText}
        </Typography>
      </Box>

      {/* Conditional Action Prompt and Buttons */}
      {isExistingFlow ? (
        <>
          {/* Existing Flow - Save Options */}
          <Typography className={styles.actionPrompt}>
            An estimation for <strong>{final_result.serial_number}</strong>{" "}
            already exists. How would you like to save this?
          </Typography>

          <Box className={styles.actionButtons}>
            <Button
              variant="contained"
              onClick={handleSaveAsNewVersion}
              disabled={agentLoading || isActionTaken}
              className={styles.primaryButton}
            >
              Save as New Version
            </Button>
            <Button
              variant="outlined"
              onClick={handleOverwriteOriginal}
              disabled={agentLoading || isActionTaken}
              className={styles.secondaryButton}
            >
              Overwrite Original
            </Button>
          </Box>
        </>
      ) : (
        <>
          {/* New Flow - Start New or Close */}
          <Typography className={styles.actionPrompt}>
            Would you like to start another estimate, or close this session?
          </Typography>

          <Box className={styles.actionButtons}>
            <Button
              variant="contained"
              onClick={handleSaveAndStartNew}
              disabled={agentLoading || isActionTaken}
              className={styles.primaryButton}
            >
              Save and Start New
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
